import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isProcedureValidationContext} from 'procedures/design/validation/procedureValidationUtils';
import {useNullableProcedureFlowchartValidationContext} from 'procedures/processes/useNullableProcedureFlowchartValidationContext';
import {BioentityForProcedure} from 'procedures/utils/useBioentitiesForProcedures';

import {StaticTemplateFlowchart} from './processFlowchartConfigEditorConstants';

export interface ApplicationFlowchartValidationContextResult {
  context: Record<string, unknown>;
  isLoaded: boolean;
  /**
   * A key representing the current state of the validation context.
   * Used to detect when the context has changed substantively (e.g., new entities loaded)
   * and re-trigger validation.
   */
  contextKey: string;
}

/**
 * Generates a stable key for Procedures validation context based on loaded bioentities.
 * This allows detection of when new entities are loaded and validation should re-run.
 */
function getProcedureValidationContextKey(
  bioentitiesForProcedures: Array<BioentityForProcedure> | null
): string {
  if (bioentitiesForProcedures == null) {
    return 'unloaded';
  }
  return bioentitiesForProcedures
    .map((b) => b.id)
    .sort((a, b) => a.localeCompare(b))
    .join(',');
}

/**
 * Hook to compute validation context for flowchart validation based on execution type.
 *
 * This hook is platform-agnostic and delegates to application-specific implementations
 * based on the execution type. Application teams should provide their own implementations
 * for their specific execution types.
 *
 * @param executionType The execution type of the flowchart
 * @param draftFlowchartConfig The draft flowchart configuration
 * @returns Validation context object plus readiness flag and context key, or empty object if no application-specific implementation exists
 */
export function useApplicationFlowchartValidationContext(
  executionType: ProcessExecutionType,
  draftFlowchartConfig: StaticTemplateFlowchart
): ApplicationFlowchartValidationContextResult {
  const procedureValidationContext = useNullableProcedureFlowchartValidationContext(draftFlowchartConfig);

  if (executionType === ProcessExecutionType.PROCEDURE) {
    const isLoaded = isProcedureValidationContext(procedureValidationContext);
    const bioentities = procedureValidationContext.bioentitiesForProcedures as
      | Array<BioentityForProcedure>
      | null
      | undefined;
    return {
      context: procedureValidationContext,
      isLoaded,
      contextKey: getProcedureValidationContextKey(bioentities ?? null),
    };
  }

  return {
    context: {},
    isLoaded: true,
    contextKey: 'default',
  };
}
